/**
 * The ONE vocabulary for why a figure is unknown or partial (SEN-433, dashboard spec §5.3; owner rulings
 * 2026-10-13 and 2026-10-21, "never refuse; return the answer with how it was generated"). Every surface that
 * shows a "—" or a coverage note takes its words from here: the Poiesis summary strip, the record figures, the
 * KPI card's default, and Sentinel's aggregates (`@poiesis/views/answerNotes`). Reshape only: every text is the
 * engine's own answer (its warnings, its refusal), never a guess.
 *
 * Not to be confused with `coverageNoteFor` (CartesianRenderer): that one is TIME coverage of a chart.
 */

type Row = Record<string, unknown>

/** The engine's frame warning, as the summary reads it. */
export type AnswerWarning = { kind: string; human: string; severity: string; structured?: Record<string, unknown> | null }

/** The one shared default: a figure with no value and nothing more specific to say. */
export const NO_DATA_REASON = 'No data in this period.'
/** A total the summary does not ask because the collection's filter has no query-API form. */
export const NOT_TOTALLED_REASON = 'Not totalled for this filter.'

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null)
/** A cell's scalar (`{value}` and one-element lists unwrap), as a string. */
function str(v: unknown): string | undefined {
  let s: unknown = v
  for (let guard = 0; guard < 8; guard++) {
    if (Array.isArray(s)) s = s[0]
    else if (s && typeof s === 'object' && 'value' in (s as object)) s = (s as { value: unknown }).value
    else break
  }
  return typeof s === 'string' ? s : undefined
}
const warningsOf = (meta: Row | undefined) => ((meta?.warnings as Row[] | undefined) ?? [])

/**
 * Why a value the screen shows as "—" is unknown: the engine names a composed metric's operand that had no data
 * (`meta.warnings[].structured = {code: 'operand_no_data', measure, operand, operands}`, owner ruling 2026-10-13).
 * {measure -> operands}.
 */
export function noDataOperands(meta: Row | undefined): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  for (const w of warningsOf(meta)) {
    const s = w?.structured as Row | undefined
    if (s?.code === 'operand_no_data' && typeof s.measure === 'string' && Array.isArray(s.operands)) {
      out[s.measure] = (s.operands as unknown[]).filter((x): x is string => typeof x === 'string')
    }
  }
  return out
}

/** Why the engine gave no value: its refusal text (422 no_telemetry / empty_scope / factor_coverage_incomplete, a
 *  memory refusal...), from an axios-shaped error, a Plenum error or a plain Error. */
export function reasonOf(e: unknown): string {
  const r = (e as { response?: { data?: { detail?: unknown; error?: { message?: string } } } })?.response?.data
  return readableReason(String(r?.error?.message ?? r?.detail ?? (e as Error)?.message ?? 'not available'))
}

/** A reason a person can read (SEN-439 X5): a bare code ("no_telemetry") is put into words, a transport failure
 *  ("Request failed with status code 500", "Network Error") says the figure could not be loaded. */
export function readableReason(text: string): string {
  if (/^Request failed with status code|^Network Error$|^timeout of \d+ms exceeded$|^Failed to fetch$/i.test(text)) {
    return 'The figure could not be loaded. Try again in a moment.'
  }
  if (/^[a-z][a-z0-9]*(?:[_-][a-z0-9]+)+$/.test(text)) {
    const words = text.replace(/[_-]+/g, ' ')
    return `${words[0].toUpperCase()}${words.slice(1)}.`
  }
  return text
}

/**
 * A total that leaves a few entities out: the engine returns the covered sum with `factor_coverage_partial` (the
 * uncovered entities carry no price / factor and hold a small share of the kWh; SEN-395 be-perf). One thin line
 * for the card, e.g. "Excludes 2 unpriced (0.5% of kWh): JYSK Fleet - Gasoline (DK), …".
 */
export function partialNote(meta: Row | undefined): string | undefined {
  for (const w of warningsOf(meta)) {
    const s = w?.structured as Row | undefined
    if (s?.code !== 'factor_coverage_partial') continue
    const n = num(s.uncovered_count) ?? 0
    const share = num(s.uncovered_share)
    const names = ((s.uncovered as Row[] | undefined) ?? []).map((u) => str(u.kenning) ?? str(u.ref)).filter(Boolean).slice(0, 2)
    const more = n > names.length ? ', …' : ''
    return `Excludes ${n} unpriced${share === null ? '' : ` (${(share * 100).toFixed(1)}% of kWh)`}`
      + (names.length ? `: ${names.join(', ')}${more}` : '')
  }
  return undefined
}

/**
 * An answer with NOTHING priced (owner ruling 2026-10-21): the engine answers NULL with `factor_coverage_none`
 * naming the factor and the entities. NULL is unknown, never 0 — the card shows "—" and this line.
 * E.g. "Nothing in scope is priced (no price value): Store A, Store B, …".
 */
export function noneNote(meta: Row | undefined): string | undefined {
  for (const w of warningsOf(meta)) {
    const s = w?.structured as Row | undefined
    if (s?.code !== 'factor_coverage_none') continue
    const factor = str(s.factor_attribute)
    const n = num(s.uncovered_count) ?? 0
    const names = ((s.uncovered as Row[] | undefined) ?? []).map((u) => str(u.kenning)).filter(Boolean).slice(0, 2)
    const more = n > names.length && names.length ? ', …' : ''
    return `Nothing in scope is priced${factor ? ` (no ${factor} value)` : ''}`
      + (names.length ? `: ${names.join(', ')}${more}` : '') + (more ? '' : '.')
  }
  return undefined
}

/** Why a 200 answer is NULL: nothing priced (`factor_coverage_none`), or the composed metric's no-data operands. */
export function noDataReason(meta: Row | undefined): string | undefined {
  const none = noneNote(meta)
  if (none) return none
  const ops = Object.values(noDataOperands(meta)).flat()
  return ops.length ? `No data for ${[...new Set(ops)].join(', ')} in this period.` : undefined
}

/**
 * The reason beside a "—": the refusal when the question failed, else what the answer's own warnings say
 * (nothing priced, the no-data operands), else the one shared default. A surface with neither a frame nor an
 * error calls `unknownReason({})`.
 */
export function unknownReason({ frame, error }: { frame?: { meta?: Row } | null; error?: unknown } = {}): string {
  if (error !== undefined && error !== null) return reasonOf(error)
  return (frame ? noDataReason(frame.meta) : undefined) ?? NO_DATA_REASON
}

/**
 * One measure's notes from a multi-measure answer: `notes` are every warning naming this metric (they ride in
 * the hover); `shown` drops the info-level ones and the comparison window's own notes, which never take the
 * figure's note line.
 */
export function measureWarnings(warnings: readonly AnswerWarning[], metric: string | undefined): {
  notes: AnswerWarning[]; shown: AnswerWarning[]
} {
  const notes = warnings.filter((w) => (w.structured as { metric?: unknown } | null | undefined)?.metric === metric)
  const shown = notes.filter((w) => w.severity !== 'info' && !w.kind.startsWith('compare')
    && (w.structured as { scope?: unknown } | null | undefined)?.scope !== 'compare' && !/^Comparison window/.test(w.human))
  return { notes, shown }
}

/** The reason beside one measure's "—": its first shown note, else any note naming it, else the shared default. */
export function measureReason({ notes, shown }: { notes: readonly AnswerWarning[]; shown: readonly AnswerWarning[] }): string {
  return shown[0]?.human ?? notes[0]?.human ?? unknownReason({})
}

/** The note under one measure's figure when the engine says it is partial. */
export function measurePartialNote(shown: readonly AnswerWarning[]): string | undefined {
  return shown.length > 0 ? `Partial: ${shown[0].human}` : undefined
}

/** An engine note made readable for a card: the leading attribute slug and its quotes go, and only the
 *  first clause is shown (the full text stays in the hover). */
export function readableNote(note: string): string {
  const s = note.replace(/^(Partial:\s*)?'?[a-z][a-z0-9_]*'?:\s*/, (_m, partial) => partial ?? '')
  const first = s.split(/\s[—(]|;\s/)[0].trim()
  return first.charAt(0).toUpperCase() + first.slice(1)
}
